import {
  alias,
  custom,
  deserialize,
  list,
  object,
  optional,
  serializable,
} from 'serializr';
import { Base, PageObject } from '@testsigmainc/models';
import { MondayProjectColumnModel, MondayUserModel } from './monday.model';

class ExternalMappingFieldStatusModel {
  @serializable
  public name: string;
  @serializable(alias('self'))
  public link: string;
  @serializable
  public iconUrl: string;

  deserialize(input: any): this {
    return Object.assign(
      this,
      deserialize(ExternalMappingFieldStatusModel, input),
    );
  }
}

class ExternalMappingFieldProjectModel {
  @serializable
  public key: string;
  @serializable(alias('self'))
  public link: string;
  @serializable
  public name: string;
  @serializable(
    custom(
      (v) => {
        return v;
      },
      (v) => v,
    ),
  )
  public avatarUrls: Map<string, string>;

  deserialize(input: any): this {
    return Object.assign(
      this,
      deserialize(ExternalMappingFieldProjectModel, input),
    );
  }
}

class ExternalMappingFieldPrioritytModel {
  @serializable
  public iconUrl: string;
  @serializable
  public name: string;
  @serializable(alias('self'))
  public link: string;

  deserialize(input: any): this {
    return Object.assign(
      this,
      deserialize(ExternalMappingFieldPrioritytModel, input),
    );
  }
}

class ExternalMappingTicketUserModel {
  @serializable(optional(object(ExternalMappingFieldStatusModel)))
  public status: ExternalMappingFieldStatusModel;
  @serializable
  public displayName: string;
  @serializable(
    custom(
      (v) => {
        return v;
      },
      (v) => v,
    ),
  )
  public avatarUrls: Map<string, string>;

  deserialize(input: any): this {
    return Object.assign(
      this,
      deserialize(ExternalMappingTicketUserModel, input),
    );
  }
}

class ExternalMappingFieldIssueTypeModel {
  @serializable
  public id: number | string;
  @serializable
  public label: string;
  @serializable
  public iconUrl: string;
  @serializable(alias('self'))
  public link: string;

  deserialize(input: any): this {
    return Object.assign(
      this,
      deserialize(ExternalMappingFieldIssueTypeModel, input),
    );
  }
}

class ExternalMappingTicketFieldModel {
  @serializable(optional(object(ExternalMappingFieldStatusModel)))
  public status: ExternalMappingFieldStatusModel;
  @serializable
  public summary: string;
  @serializable
  public description: string;
  @serializable(optional(object(ExternalMappingFieldProjectModel)))
  public project: ExternalMappingFieldProjectModel;
  @serializable(optional(object(ExternalMappingTicketUserModel)))
  public reporter: ExternalMappingTicketUserModel;
  @serializable(optional(object(ExternalMappingTicketUserModel)))
  public assignee: ExternalMappingTicketUserModel | null;
  @serializable(optional(object(ExternalMappingFieldPrioritytModel)))
  public priority: ExternalMappingFieldPrioritytModel;
  @serializable(optional(object(ExternalMappingFieldIssueTypeModel)))
  public issuetype: ExternalMappingFieldIssueTypeModel;

  deserialize(input: any): this {
    return Object.assign(
      this,
      deserialize(ExternalMappingTicketFieldModel, input),
    );
  }
}

class ExternalMappingFieldIssueModel {
  @serializable
  public description: string;
  @serializable(alias('created_at'))
  public createdAt: string;
  @serializable
  public key: string;
  @serializable
  public name: string;
  @serializable
  public title: string;
  @serializable(alias('updated_at'))
  public updatedAt: string;
  @serializable(alias('issue_type_id'))
  public issueTypeId: number;
  @serializable(alias('status_id'))
  public statusId: number;
  @serializable(alias('reporter_id'))
  public reportderId: number;
  @serializable(alias('project_id'))
  public projectId: number;

  deserialize(input: any): this {
    return Object.assign(
      this,
      deserialize(ExternalMappingFieldIssueModel, input),
    );
  }
}

class ExternalMappingFieldUserModel {
  @serializable
  public id: number;
  @serializable(alias('avatar_url'))
  public avatarUrl: string;
  @serializable
  public name: string;

  deserialize(input: any): this {
    return Object.assign(
      this,
      deserialize(ExternalMappingFieldUserModel, input),
    );
  }
}

class ExternalMappingFieldStatusesModel {
  @serializable
  public id: number;
  @serializable
  public description: string;
  @serializable
  public label: string;

  deserialize(input: any): this {
    return Object.assign(
      this,
      deserialize(ExternalMappingFieldStatusesModel, input),
    );
  }
}

class ExternalMappingContextModel {
  @serializable
  public id: string;
  @serializable
  public name: string;

  deserialize(input: any): this {
    return Object.assign(this, deserialize(ExternalMappingContextModel, input));
  }
}

class ExternalMappingFieldClikupStatusModel {
  @serializable
  public type: string;
  @serializable
  public status: string;

  deserialize(input: any): this {
    return Object.assign(
      this,
      deserialize(ExternalMappingFieldClikupStatusModel, input),
    );
  }
}

class ExternalMappingFieldModel {
  @serializable(optional(object(ExternalMappingFieldIssueModel)))
  public issue: ExternalMappingFieldIssueModel;
  @serializable(
    alias(
      'issue_types',
      list(optional(object(ExternalMappingFieldIssueTypeModel))),
    ),
  )
  public issueTypes: ExternalMappingFieldIssueTypeModel[];
  @serializable(optional(list(object(ExternalMappingFieldStatusesModel))))
  public statuses: ExternalMappingFieldStatusesModel[];
  @serializable(optional(list(object(ExternalMappingFieldUserModel))))
  public users: ExternalMappingFieldUserModel[];
  @serializable(optional(object(ExternalMappingTicketFieldModel)))
  public fields: ExternalMappingTicketFieldModel;
  @serializable
  public summary: string;
  @serializable
  public description: string;
  @serializable(object(ExternalMappingFieldUserModel))
  public reporter: ExternalMappingFieldUserModel;
  @serializable
  public created: number;
  @serializable
  public updated: number;
  @serializable
  public idReadable: string;
  @serializable(alias('$type'))
  public type: string;
  @serializable
  public name: string;
  @serializable
  public id: string;
  @serializable(optional(object(ExternalMappingContextModel)))
  public folder: ExternalMappingContextModel;
  @serializable(optional(object(ExternalMappingContextModel)))
  public list: ExternalMappingContextModel;
  @serializable(optional(object(ExternalMappingContextModel)))
  public project: ExternalMappingContextModel;
  @serializable
  public url: string;
  @serializable(optional(object(ExternalMappingFieldClikupStatusModel)))
  public status: ExternalMappingFieldClikupStatusModel;
  @serializable(alias('date_created'))
  public createdAt: string;
  @serializable(alias('date_updated'))
  public updatedAt: string;
  @serializable(optional(object(ExternalMappingContextModel)))
  public board: ExternalMappingContextModel;
  @serializable(alias('team_id'))
  public teamId: string;
  @serializable(optional(object(ExternalMappingContextModel)))
  public space: ExternalMappingContextModel;
  @serializable(
    alias('column_values', list(optional(object(MondayProjectColumnModel)))),
  )
  public columnValues: MondayProjectColumnModel[];
  @serializable
  public title: string;
  @serializable(list(optional(object(MondayUserModel))))
  public subscribers: MondayUserModel;

  deserialize(input: any): this {
    return Object.assign(this, deserialize(ExternalMappingFieldModel, input));
  }
}

export class ExternalMappingModel extends Base implements PageObject {
  @serializable
  public applicationId: number;
  @serializable
  public entityId: number;
  @serializable
  public externalId: string;
  @serializable(
    custom(
      (v) => v,
      (v) => v,
    ),
  )
  public fields: any;
  @serializable(
    custom(
      (v) => {
        return v ? JSON.parse(v) : {};
      },
      (v) => JSON.parse(v),
    ),
  )
  public misc: any;

  deserialize(input: any): this {
    return Object.assign(this, deserialize(ExternalMappingModel, input));
  }
}
